import {normalize, prepareProduct, findMatches} from './es-search-core.js';

if (!customElements.get('es-collection-search')) {
  customElements.define('es-collection-search', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.globalSearch = this.dataset.scope === 'all';
      this.form = this.querySelector('form');
      this.input = this.querySelector('[name="q"]');
      this.clearButton = this.querySelector('.es-csearch__clear');
      this.panel = this.querySelector('[role="listbox"]');
      this.results = this.querySelector('.es-csearch__results');
      this.status = this.results.querySelector('[role="status"]');
      this.grid = this.results.querySelector('ul');
      this.sort = this.results.querySelector('select');
      this.more = this.results.querySelector('button');
      this.bar = this.closest('.es-filterbar');
      if(this.bar)this.bar.after(this.results);
      this.filterLinks=this.bar?[...this.bar.querySelectorAll('a')].map(link=>({link,href:link.href})):[];
      this.allResults=this.querySelector('.es-csearch__all');
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.money = new Intl.NumberFormat(this.dataset.locale || 'ar', {style:'currency',currency:this.dataset.currency || 'EGP',maximumFractionDigits:0});
      this.input.value = this.readQuery();
      this.form.addEventListener('submit', event => {event.preventDefault(); clearTimeout(this.timer); if(this.globalSearch){if(normalize(this.input.value))location.assign(this.globalResultsUrl());return;} this.close(); this.update(false,true);}, {signal});
      this.input.addEventListener('input', () => { clearTimeout(this.timer); this.timer=setTimeout(()=>this.update(true),200); }, {signal});
      this.input.addEventListener('focus', () => { this.load().catch(()=>{}); if(this.input.value) this.update(true); }, {signal});
      this.input.addEventListener('keydown', event=>this.keydown(event), {signal});
      this.clearButton.addEventListener('click', () => {this.input.value=''; this.update(false); this.input.focus();}, {signal});
      this.sort.addEventListener('change',()=>this.update(false),{signal});
      this.more.addEventListener('click',()=>{this.limit+=24; this.renderGrid();},{signal});
      document.addEventListener('click',event=>{if(!this.contains(event.target)) this.close();},{signal});
      this.bar?.addEventListener('toggle',event=>{if(event.target.matches('details[open]'))this.close();},{capture:true,signal});
      this.bar?.addEventListener('click',event=>this.filterLink(event),{capture:true,signal});
      if(!this.globalSearch)window.addEventListener('popstate',()=>{this.input.value=this.readQuery();this.update(false);},{signal});
      if(this.input.value) this.update(false);
    }
    disconnectedCallback() {
      this.controller?.abort();
      clearTimeout(this.timer);
      this.results?.remove();
      this.native?.removeAttribute('data-es-native-hidden');
    }
    globalResultsUrl() {
      const url=new URL(this.dataset.allUrl,location.origin);
      url.hash='es-search='+encodeURIComponent(this.input.value.trim());
      return url.href;
    }
    readQuery() {
      if(this.globalSearch)return '';
      const url=new URL(location.href);
      return new URLSearchParams(url.hash.slice(1)).get('es-search') || url.searchParams.get('es_q') || '';
    }
    async load() {
      if(this.products) return this.products;
      if(this.pending) return this.pending;
      this.pending=(async()=>{
        const base=this.globalSearch?new URL(this.dataset.allUrl,location.origin):new URL(location.href);
        base.searchParams.delete('es_q');
        base.searchParams.set('view','es-search-index');
        const read=async(page)=>{
          const url=new URL(base); url.searchParams.set('page',String(page));
          const response=await fetch(url,{signal:AbortSignal.any([this.controller.signal,AbortSignal.timeout(20000)])});
          if(!response.ok)throw Error('Search index unavailable');
          const data=JSON.parse(await response.text());
          if(!Array.isArray(data.products)||data.page!==page||!Number.isInteger(data.pages))throw Error('Invalid search index');
          return data;
        };
        const first=await read(1);
        if(first.total>25000 || first.pages>100)throw Error('Narrow the filters');
        let products=first.products;
        for(let page=2;page<=first.pages;page+=2){
          const pages=await Promise.all([read(page),...(page<first.pages?[read(page+1)]:[])]);
          for(const data of pages)products.push(...data.products);
        }
        products=Array.from(new Map(products.map(p=>[p.id,p])).values());
        if(products.length!==first.total)throw Error('Incomplete search index');
        this.products=products.map(prepareProduct);
        return this.products;
      })().finally(()=>{this.pending=null;});
      return this.pending;
    }
    async update(suggest=false,submitted=false) {
      const version=this.version=(this.version || 0)+1;
      const query=this.input.value.trim();
      const valid=!!normalize(query);
      const url=new URL(location.href);
      url.searchParams.delete('es_q');
      if(valid)url.hash='es-search='+encodeURIComponent(query);else if(url.hash.startsWith('#es-search='))url.hash='';
      if(!this.globalSearch)history.replaceState(history.state,'',url);
      if(this.allResults)this.allResults.href=this.globalResultsUrl();
      this.clearButton.hidden=!query;
      this.filterLinks.forEach(({link,href})=>{link.href=href;this.filterLink({target:link});});
      this.close();
      this.native=this.globalSearch?null:document.querySelector('collection-component');
      this.results.hidden=!valid;
      if(!valid){this.native?.removeAttribute('data-es-native-hidden');return;}
      this.native?.setAttribute('data-es-native-hidden','');
      this.status.textContent=this.dataset.loading;
      this.grid.replaceChildren();this.more.hidden=true;
      this.results.setAttribute('aria-busy','true');
      try {
        await this.load();
        if(version!==this.version || !this.isConnected)return;
        this.matches=findMatches(this.products,query,this.sort.value);
        this.limit=24;
        this.status.textContent=this.matches.length?`${this.dataset.count} ${this.matches.length}`:this.dataset.empty;
        if(!this.globalSearch)this.renderGrid();
        if((suggest || this.globalSearch) && document.activeElement===this.input)this.suggest();
        if(submitted)this.results.scrollIntoView({behavior:'smooth',block:'start'});
      } catch(error) {
        if(version!==this.version || !this.isConnected)return;
        this.status.textContent=this.dataset.error;
      } finally {if(version===this.version)this.results.removeAttribute('aria-busy');}
    }
    node(tag,text,className) {const e=document.createElement(tag);if(text)e.textContent=text;if(className)e.className=className;return e;}
    image(product) {const image=this.node('img');image.src=product.image;image.alt='';image.loading='lazy';image.width=540;image.height=540;return image;}
    renderGrid() {
      this.grid.replaceChildren();
      for(const product of this.matches.slice(0,this.limit)){
        const card=this.node('li',null,'es-csearch__card');
        const link=this.node('a');link.href=product.url;
        if(product.image)link.append(this.image(product));
        link.append(this.node('h3',product.title));card.append(link);
        card.append(this.node('p',`${product.varies?this.dataset.from+' ':''}${this.money.format(product.price/100)}`,'es-csearch__price'));
        if(product.compare>product.price)card.append(this.node('del',this.money.format(product.compare/100)));
        if(!product.available)card.append(this.node('small',this.dataset.sold));
        const view=this.node('a',this.dataset.view,'es-csearch__view');view.href=product.url;view.setAttribute('aria-label',`${this.dataset.view}: ${product.title}`);card.append(view);
        this.grid.append(card);
      }
      this.more.hidden=this.matches.length<=this.limit;
    }
    suggest() {
      this.panel.replaceChildren();
      this.matches.slice(0,this.globalSearch?8:5).forEach((product,index)=>{
        const li=this.node('li');li.setAttribute('role','presentation');
        const link=this.node('a');link.href=product.url;link.id=`${this.input.id}-option-${index}`;link.setAttribute('role','option');link.setAttribute('aria-selected','false');link.tabIndex=-1;
        if(product.image)link.append(this.image(product));
        const details=this.node('span',null,'es-csearch__suggestion-details');
        details.append(this.node('span',product.title));
        details.append(this.node('strong',`${product.varies?this.dataset.from+' ':''}${this.money.format(product.price/100)}`,'es-csearch__suggestion-price'));
        link.append(details);li.append(link);this.panel.append(li);
      });
      this.panel.hidden=!this.matches.length;
      this.input.setAttribute('aria-expanded',String(!this.panel.hidden));this.selected=-1;
    }
    close() {this.panel.hidden=true;this.input.setAttribute('aria-expanded','false');this.input.removeAttribute('aria-activedescendant');this.selected=-1;}
    keydown(event) {
      if(event.key==='Escape'){event.preventDefault();if(this.globalSearch&&!this.panel.hidden)event.stopPropagation();this.close();return;}
      if(event.key==='Tab'){this.close();return;}
      if(this.panel.hidden)return;
      const options=[...this.panel.querySelectorAll('[role="option"]')];
      if(event.key==='ArrowDown'||event.key==='ArrowUp'){
        event.preventDefault();this.selected=(this.selected+(event.key==='ArrowDown'?1:-1)+options.length)%options.length;
        options.forEach((option,index)=>option.setAttribute('aria-selected',String(index===this.selected)));
        this.input.setAttribute('aria-activedescendant',options[this.selected].id);options[this.selected].scrollIntoView({block:'nearest'});
      }else if(event.key==='Enter'&&this.selected>=0){event.preventDefault();location.assign(options[this.selected].href);}
    }
    filterLink(event) {
      const link=event.target.closest('a');
      if(!link || !this.bar.contains(link) || link.closest('es-collection-search'))return;
      const url=new URL(link.href);
      if(url.origin!==location.origin)return;
      const query=this.input.value.trim();
      if(!link.classList.contains('es-filters__clear')) {
        url.searchParams.delete('es_q');
        url.hash=query?'es-search='+encodeURIComponent(query):'';
        const current=new URL(location.href);
        const drops=[...this.bar.querySelectorAll('[data-es-drop]')];
        const isPrice=link.closest('[data-es-drop]')===drops.at(-1);
        if(!isPrice)for(const key of ['filter.v.price.gte','filter.v.price.lte'])if(current.searchParams.has(key))url.searchParams.set(key,current.searchParams.get(key));
      } else {url.searchParams.delete('es_q');url.hash='';}
      link.href=url.href;
    }
  });
}
